// Thin REST client for the endpoints J9's screens need (friends, blocks,
// calls, sessions, account) that J7's AuthClient doesn't cover (docs/02 §3).
// Every request/response body is a @merkury/protocol zod schema — this module
// never redefines a wire shape, it only calls the server and validates.
//
// Auth: bearer access token from an injected `AccessTokenProvider` (the same
// shape the call engine takes — see src/call/types.ts) so both the call
// engine and these screens share one token source at wiring time.

import type { z } from 'zod';
import { defaultFetch, type ResilientInit } from '../lib/http.js';
import {
  AliasesBlobResponse,
  ApiError as ApiErrorSchema,
  AuthChallengeResponse,
  BlocksListResponse,
  CallsListResponse,
  CallsSeenRequest,
  DeleteMeRequest,
  FriendRequestsListResponse,
  FriendsListResponse,
  MeResponse,
  PasskeyLoginOptionsResponse,
  PasskeyLoginResponse,
  PasskeyRegisterOptionsResponse,
  PasskeyRegisterResponse,
  PasskeysListResponse,
  PutAliasesBlobResponse,
  SendFriendRequestRequest,
  SessionsListResponse,
  SetPasswordRequest,
  TurnCredentialsResponse,
  UpdateSessionRequest,
  type BlockRequest,
  type ErrorCode,
  type PutAliasesBlobRequest,
  type UpdateMeRequest,
  type PasskeyLoginRequest,
  type PasskeyRegisterRequest,
  type SessionKind,
} from '@merkury/protocol';

/** REST error with the protocol `ErrorCode` when the server supplied one. */
export class RestError extends Error {
  readonly status: number;
  readonly code: ErrorCode | undefined;

  constructor(status: number, code: ErrorCode | undefined, message: string) {
    super(message);
    this.name = 'RestError';
    this.status = status;
    this.code = code;
  }
}

/** Supplies a currently-valid JWT access token (shared shape with the call engine). */
export type AccessTokenProvider = () => Promise<string>;

// --- REST origin selection (docs/04 §2) --------------------------------------
//
// The signed endpoint list used to protect only the WebSocket: REST went to one
// build-time origin with no failover, so a censor that freezes new TLS flows to
// the primary host left the app connected (live socket, live presence) and
// unable to load a single list. Everything below makes REST ride the SAME list
// the signaling race already proved — mirroring what native Android does off the
// persisted state (reach/ReachEndpoints.restBaseFromWsUrl).

/** Path prefix every REST route hangs off — Caddy routes `/api/*` to the Go
 *  server and docs/02 puts REST under `/v1` (frontend/app shell/config.ts). */
export const REST_PATH_PREFIX = '/api/v1';

/** scheme + authority of an absolute URL; `[^/?#]+` stops before the path. */
const AUTHORITY_RE = /^(wss|ws|https|http):\/\/([^/?#]+)/i;

/**
 * REST base on the same authority as a signaling URL: `wss`→`https`,
 * `ws`→`http`, authority (port included) kept verbatim, `/api/v1` appended.
 * Same semantics as the native Android derivation, so both stacks follow one
 * winner. Hand-rolled instead of `new URL` because the iOS brain runs in a bare
 * JSContext with no URL constructor. An authority carrying userinfo is refused
 * — no credentials in URLs (docs/03 §5).
 */
export function restBaseFromWsUrl(wsUrl: string): string | null {
  const match = AUTHORITY_RE.exec(wsUrl.trim());
  if (!match) return null;
  const scheme = match[1]!.toLowerCase();
  const authority = match[2]!;
  if (authority.includes('@')) return null;
  return `${scheme === 'ws' || scheme === 'http' ? 'http' : 'https'}://${authority}${REST_PATH_PREFIX}`;
}

/** What an IP-literal endpoint is called in a log line or a snapshot. */
const IP_LABEL = 'ip';

/**
 * True unless the authority provably ends in a DNS label. Deliberately
 * over-inclusive — it decides whether a string may be PRINTED: an IPv6
 * authority is bracketed or carries colons, and neither a dotted quad nor its
 * decimal/hex encodings end in an alphabetic label, while a real TLD never
 * starts with a digit (RFC 3696 §2). Anything that is not provably a hostname
 * is treated as an address.
 */
function isAddressLiteral(authority: string): boolean {
  const host = authority.startsWith('[') ? authority : authority.replace(/:\d+$/, '');
  if (host.startsWith('[') || host.includes(':')) return true; // IPv6 literal
  const last = host.split('.').pop() ?? '';
  return !/^[a-z][a-z0-9-]*$/.test(last);
}

/**
 * Authority of a base URL, for dev diagnostics. A relative base (`/api/v1`, the
 * same-origin web default) has none.
 *
 * The endpoint list may name an endpoint by ADDRESS (protocol endpoints.ts
 * `kind: 'ip'`), and "no tokens, IPs, or UUIDs in logs" (CLAUDE.md) admits no
 * exception for a public one — so an address literal comes back as `ip`. This
 * is the only function that turns a base into something printable, which is
 * what makes that provable: every caller below goes through it.
 */
export function hostOfBase(base: string): string | null {
  const match = AUTHORITY_RE.exec(base);
  if (!match) return null;
  const authority = match[2]!.toLowerCase();
  return isAddressLiteral(authority) ? IP_LABEL : authority;
}

/** Where the next REST request goes, and how a dead origin is abandoned. */
export interface RestOrigin {
  /** Base for the NEXT request — version prefix included, no trailing slash. */
  base(): string;
  /**
   * A transport-level failure (our timeout, DNS, TLS, reset) against `base`.
   * Moves the NEXT request onto another candidate; the failed request still
   * throws and is never replayed here — `POST /auth/refresh` rotates the
   * refresh token server-side, and a duplicate rotation kills the whole
   * session family (docs/03 §3).
   */
  noteTransportFailure(base: string): void;
  /**
   * An HTTP response arrived from `base`. `authored` is true when OUR server
   * plainly wrote it: any success, or an error body carrying a protocol `code`
   * — httpapi `respond.go writeError` is the only path the server emits errors
   * through, so a coded body is its signature (the same test `account.ts`
   * `isSessionDeath` already trusts).
   *
   * An UNAUTHORED answer on an origin that has never yet answered us properly
   * is a host that does not serve this API on this network — an edge that
   * proxies `/ws` but not `/api/*`, a null-route, a block page. That rotates
   * the NEXT request, like a transport failure. Nothing rotates once an origin
   * has been proven: a proven origin's 404 is a missing row, not a dead host.
   */
  noteResponse(base: string, authored: boolean): void;
}

const trimBase = (url: string): string => url.replace(/\/$/, '');

/** The one-origin case (the web shell, tests): whatever was configured, forever. */
export function staticOrigin(baseUrl: string): RestOrigin {
  const base = trimBase(baseUrl);
  return {
    base: () => base,
    noteTransportFailure: () => undefined,
    noteResponse: () => undefined,
  };
}

export interface OriginRouterOptions {
  /** Build-time REST base. The bootstrap default AND the last resort — it is
   *  the only origin a fresh install with no persisted state has. */
  configured: string;
  /** Log sink for origin changes. PUBLIC endpoint hostnames only. */
  log?: (message: string) => void;
}

/** The persisted signaling state (call/types.ts EndpointListStorage), narrowed
 *  to what the origin order needs. */
export interface EndpointRouteState {
  list?: { endpoints: readonly { url: string }[] } | undefined;
  lastGoodUrl?: string | undefined;
}

/**
 * Dynamic REST origin, ordered — no second racing engine, one predictable list:
 *
 *   1. the origin derived from the signaling winner (`lastGoodUrl`), if any —
 *      the freshest proof of what this network actually lets through;
 *   2. the configured build-time base — evidence-free but deliberate, so it
 *      still outranks a mirror nothing has tried, and a client with no winner
 *      yet behaves exactly as it always did;
 *   3. the rest of the endpoint list, in list order.
 *
 * Duplicates collapse to their first position. A transport failure steps one
 * place down the list (wrapping); a new winner restarts at the top. Success
 * pins by doing nothing — a working origin is never left for a hopeful one.
 */
export class OriginRouter implements RestOrigin {
  private readonly configured: string;
  private readonly log: (message: string) => void;
  private winnerWsUrl: string | null = null;
  private listBases: string[] = [];
  private order: string[];
  private index = 0;
  /** Origins that have answered with something our server wrote. Proven once,
   *  proven for good: only an origin that has never served us can be abandoned
   *  over an HTTP status, so a genuine 404 (or a 5xx blip) on a working host
   *  never costs the session its origin. */
  private readonly proven = new Set<string>();

  constructor(options: OriginRouterOptions) {
    this.configured = trimBase(options.configured);
    this.log = options.log ?? ((): void => undefined);
    this.order = [this.configured];
  }

  base(): string {
    return this.order[this.index] ?? this.configured;
  }

  /** Adopt a new endpoint list and/or race winner. Called at bring-up with the
   *  persisted state and on every `persist` the signaling client fires. */
  noteEndpointState(state: EndpointRouteState): void {
    const previous = this.base();
    const previousWinner = this.winnerBase();
    if (state.lastGoodUrl !== undefined) this.winnerWsUrl = state.lastGoodUrl;
    if (state.list) {
      this.listBases = state.list.endpoints
        .map((endpoint) => restBaseFromWsUrl(endpoint.url))
        .filter((base): base is string => base !== null);
    }
    this.rebuild();
    const winner = this.winnerBase();
    if (winner !== null && winner !== previousWinner) {
      this.index = 0;
      if (this.base() !== previous) this.log(`rest: origin follows signaling winner ${this.label()}`);
      return;
    }
    // Keep pointing at whatever we were using; only a dropped origin resets.
    const at = this.order.indexOf(previous);
    this.index = at >= 0 ? at : 0;
  }

  noteTransportFailure(base: string): void {
    this.rotate(base, 'rest: rotating origin to');
  }

  noteResponse(base: string, authored: boolean): void {
    if (authored) {
      this.proven.add(base);
      return;
    }
    if (this.proven.has(base)) return;
    this.rotate(base, 'rest: origin does not serve this API, moving to');
  }

  /** Step one place down the list, at most once per origin per burst. */
  private rotate(base: string, why: string): void {
    // Guarded so a burst of parallel failures on one origin rotates once, not
    // once per request.
    if (base !== this.base() || this.order.length < 2) return;
    this.index = (this.index + 1) % this.order.length;
    this.log(`${why} ${this.label()}`);
  }

  /** Host REST is sending to (dev diagnostics; null for a relative base). */
  get restHost(): string | null {
    return hostOfBase(this.base());
  }

  /** Host of the endpoint the signaling race last won on (dev diagnostics). */
  get wsHost(): string | null {
    return this.winnerWsUrl === null ? null : hostOfBase(this.winnerWsUrl);
  }

  /** True while REST is on an endpoint-list origin rather than the built-in one. */
  get derived(): boolean {
    return this.base() !== this.configured;
  }

  private label(): string {
    return this.restHost ?? 'same-origin';
  }

  private winnerBase(): string | null {
    return this.winnerWsUrl === null ? null : restBaseFromWsUrl(this.winnerWsUrl);
  }

  private rebuild(): void {
    const order: string[] = [];
    const push = (base: string | null): void => {
      if (base !== null && !order.includes(base)) order.push(base);
    };
    push(this.winnerBase());
    push(this.configured);
    for (const base of this.listBases) push(base);
    this.order = order;
  }
}

/** The URL a fetch argument names, without touching `URL`/`Request` (absent in
 *  the iOS JSContext). */
function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  const url = (input as { url?: unknown }).url;
  return typeof url === 'string' ? url : String(input);
}

/** The abort signal a call was made with, from either argument form. */
function signalOf(input: RequestInfo | URL, init?: RequestInit): AbortSignal | undefined {
  return init?.signal ?? (input as { signal?: AbortSignal | null }).signal ?? undefined;
}

/**
 * Decorate a `fetch` so a transport failure against the current origin rotates
 * the NEXT request onto another candidate. It adds no retry of its own: the
 * failed request still rejects, unchanged, so nothing — least of all
 * `POST /auth/refresh` — is ever replayed by rotation.
 *
 * A response of any status proves nothing here beyond "this host answered"; a
 * success proves the origin outright, and the judgement about an ERROR body
 * needs the body, so it is made where the body is parsed (`RestClient` and
 * `account.ts`).
 *
 * A caller's own abort is not the origin's fault and never rotates — read off
 * the request's signal, which is exactly how `resilientFetch` decides an
 * attempt was `cancelled` rather than failed (lib/http.ts).
 */
export function routeFailures(origin: RestOrigin, base: typeof fetch): typeof fetch {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const at = origin.base();
    const ours = requestUrl(input).startsWith(at);
    try {
      const response = await base(input, init);
      if (ours && response.ok) origin.noteResponse(at, true);
      return response;
    } catch (err) {
      if (ours && signalOf(input, init)?.aborted !== true) origin.noteTransportFailure(at);
      throw err;
    }
  };
}

/** Whether an error response carries the protocol `code` only our server
 *  writes. For the paths that discard the parsed error and would otherwise
 *  learn nothing from the body. */
async function authoredByServer(response: Response): Promise<boolean> {
  try {
    return ApiErrorSchema.safeParse(await response.json()).success;
  } catch {
    return false;
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

// --- REST over the signaling socket (protocol ws.ts `sys.rpc`) ---------------
//
// The second transport. A request that rides the socket the client already
// holds pays no TLS handshake, opens no second connection to a host whose
// simultaneous connections may be policed, and costs a few hundred bytes rather
// than a fresh exchange — which is why it survives all three documented Russian
// blocking mechanisms where a new HTTPS request does not (docs/04 §1). In the
// field incident that socket carried presence for minutes while every REST call
// died at our own timeout.
//
// It is a TRANSPORT, not a second API: same route, same handler, same status
// vocabulary. Everything about which requests may take it is below.

/** A REST call as the socket carries it. `path` is the route beneath the
 *  version prefix — the socket determines which server it reaches. */
export interface RpcRequest {
  method: Method;
  path: string;
  body?: unknown;
}

/** Why a tunnelled request produced no answer. */
export type RpcFailure =
  /** No socket, or the transport has stood down (server one deploy behind). */
  | 'unavailable'
  /**
   * The GATEWAY refused it, not the route: the server answered `sys.rpc` with
   * 501 because no dispatcher is installed (backend ws/rpc.go — an explicitly
   * supported state). Answered before dispatch, so the route did not run.
   */
  | 'unsupported'
  /**
   * The TUNNEL's own credential was refused, not the caller's request: the
   * socket presents the access token it authenticated with (backend rpcws `Do`
   * replays it as the Bearer) and the auth middleware answered 401 BEFORE the
   * route ran. A 15-minute token under a socket with no maximum lifetime makes
   * that ordinary and recoverable, and — like `unsupported` — it says nothing
   * about the route, so the request may still be issued over HTTPS.
   */
  | 'unauthorized'
  /**
   * Too big for the socket in one direction or the other: declined here before
   * the write, or dispatched and answered 502 because the response exceeded the
   * server's 32 KB result ceiling (backend rpcws `TooLarge`). Which of the two
   * it was is carried by `sent`, never guessed from this.
   */
  | 'oversize'
  /** Refused before the write: the server cannot have seen it. */
  | 'unsent'
  /** Written, no answer inside the deadline. */
  | 'timeout'
  /** The socket died under it. */
  | 'closed';

export type RpcOutcome =
  | { ok: true; status: number; body?: unknown }
  | {
      ok: false;
      failure: RpcFailure;
      /** True when the frame reached the socket. The single fact replay safety
       *  turns on: once written, a lost response and a lost request are
       *  indistinguishable. */
      sent: boolean;
    };

/** The signaling client's tunnel, injected here so this module stays free of
 *  the call engine (`SignalingClient.rpcTransport` implements it). */
export interface RpcTransport {
  /** True when a request would ride an established, authenticated socket now. */
  ready(): boolean;
  /** Never rejects: the outcome carries the failure, including whether the
   *  request was written. */
  send(request: RpcRequest): Promise<RpcOutcome>;
  /**
   * The socket's own credential aged out under it (a tunnelled 401). Install
   * the session's current access token on the live connection (`sys.reauth`)
   * and resolve true only when a DIFFERENT one actually reached the wire —
   * which is what makes re-issuing the refused request a first issue rather
   * than a second one. Optional: a transport that cannot renew reports nothing
   * and the caller falls back to HTTPS.
   */
  renewAuth?(): Promise<boolean>;
}

/** Methods with no server-side effect, so issuing one twice cannot double-apply. */
const SAFE_METHODS = new Set<string>(['GET', 'HEAD', 'OPTIONS']);

/**
 * Replay-proof whatever the method says. `POST /auth/refresh` consumes the
 * refresh token it presents; a second rotation kills the whole session family
 * (docs/03 §3). lib/http.ts enforces this for the HTTPS chain — restated here
 * because a socket that drops mid-request is a SECOND chain, and the invariant
 * is "never replayed on any path", not "never replayed over HTTPS".
 */
const NEVER_REPLAY = /\/auth\/refresh$/;

/** The route a path names, without its query or fragment. */
const routeOf = (path: string): string => (path.split('#')[0] ?? '').split('?')[0] ?? '';

/**
 * May a request the socket did not answer be issued again, over HTTPS?
 *
 * `sent === false` is the one case where any method qualifies, and the
 * transport can PROVE it: the frame never reached `ws.send`, so the server
 * cannot have seen it and issuing it now is the first issue, not a replay.
 *
 * Once written, the wire cannot tell a lost response from a lost request, so
 * only a method with no server-side effect may go again — and `/auth/refresh`
 * may not, ever, on any transport, whatever its method. A refresh that was
 * written and lost fails; the account layer asks again later with the token it
 * still holds, which is exactly what a lost HTTPS refresh does today.
 *
 * `failure: 'unsupported'` and `failure: 'unauthorized'` are the two cases where
 * a WRITTEN request may go again whatever its method, and for the same reason:
 * the gateway answered before it dispatched anything — 501 because no
 * dispatcher is installed, 401 because the socket's own credential was refused
 * by the auth middleware — so the route provably did not run. Note the order of
 * the rules below: the never-replay route is decided FIRST, on `sent` alone,
 * because that is a fact this client can prove; a status is the server's word
 * about its own state, and the one request that can kill a session family does
 * not turn on anybody's word.
 *
 * Exported because the auth layer runs its own chain and must decide the same
 * way from the same rule, not from a second copy of it.
 */
export function mayReissueOverHttps(
  method: string,
  path: string,
  outcome: { sent: boolean; failure?: RpcFailure },
): boolean {
  if (NEVER_REPLAY.test(routeOf(path))) return !outcome.sent;
  if (outcome.failure === 'unsupported' || outcome.failure === 'unauthorized') return true;
  if (!outcome.sent) return true;
  return SAFE_METHODS.has(method.toUpperCase());
}

/**
 * A tunnelled 401 that is about the SOCKET's credential rather than the route's
 * answer — the one status that must not be handed back the way an HTTPS 401 is.
 *
 * A tunnelled request is authorized PER REQUEST, with the access token the
 * socket presented at `sys.auth`; that token lasts 15 minutes and the socket has
 * no maximum lifetime, so an aged connection answering 401 to everything is
 * ordinary, recoverable, and must never reach a screen as "you are signed out".
 *
 * Two guards keep it from stealing a real answer:
 *
 *  - the protocol `code`. `unauthorized` is written by the auth middleware,
 *    which runs BEFORE the handler; a route's own 401 carries its own code
 *    (`password_required`, docs/03 §3a) and is left alone.
 *  - `/auth/refresh`, which authenticates itself with the refresh token in its
 *    BODY, not with the socket's credential — its 401 is the route answering,
 *    and re-issuing it would rotate a second time (docs/03 §3).
 */
function staleTunnelCredential(path: string, outcome: RpcOutcome): boolean {
  if (!outcome.ok || outcome.status !== 401) return false;
  if (NEVER_REPLAY.test(routeOf(path))) return false;
  const error = ApiErrorSchema.safeParse(outcome.body);
  return error.success && error.data.code === 'unauthorized';
}

/** What an unrecoverable tunnel 401 becomes: a transport failure whose route
 *  provably did not run, so HTTPS may carry it (mayReissueOverHttps). */
const TUNNEL_UNAUTHORIZED: RpcOutcome = { ok: false, failure: 'unauthorized', sent: true };

/**
 * A tunnelled answer, decoded exactly as the HTTPS path decodes an HTTP one —
 * same statuses, same `ApiError` body, same `RestError`. One error vocabulary
 * is the reason `sys.rpc.result` carries an HTTP status at all; a caller must
 * never be able to tell which transport served it.
 */
function decodeRpc<S extends z.ZodType>(
  path: string,
  status: number,
  body: unknown,
  responseSchema: S,
): z.infer<S> {
  if (status < 200 || status >= 300) {
    const error = ApiErrorSchema.safeParse(body);
    throw new RestError(
      status,
      error.success ? error.data.code : undefined,
      error.success ? error.data.message : `request to ${path} failed with status ${status}`,
    );
  }
  if (status === 204) return responseSchema.parse(undefined);
  const parsed = responseSchema.safeParse(body);
  if (!parsed.success) {
    throw new RestError(status, undefined, `malformed response from ${path}`);
  }
  return parsed.data;
}

/**
 * The socket lost a request that may not be issued again. It is a network
 * failure and nothing more specific: `AbortError` is what `runtime/codes.ts`
 * already maps to the NETWORK code, so the UI says "the network is not working"
 * rather than inventing a status the server never sent. The message names no
 * route — paths carry user ids (docs/03 §5).
 */
function rpcFailed(failure: RpcFailure): Error {
  const err = new Error(`socket request ${failure}`);
  err.name = 'AbortError';
  return err;
}

/**
 * Routes kept off the socket regardless of how good it looks. The alias blob is
 * up to 87 400 chars in EITHER direction (protocol rest.ts) against a 64 KB
 * frame ceiling, and a body that size on the socket is the head-of-line stall
 * this transport's whole design avoids. Size, not policy: everything else is
 * bounded by RPC_MAX_REQUEST_BYTES inside the transport, which declines without
 * writing and so costs the caller nothing but an HTTPS request.
 *
 * Nothing else needs listing for its RESPONSE size — an unpaginated list that
 * outgrows the server's 32 KB result ceiling comes back 502 and is re-issued
 * over HTTPS by `mayReissueOverHttps` (it is a GET), so a user with hundreds of
 * friends loses a round trip, not the screen.
 */
const HTTPS_ONLY_ROUTE = /^\/me\/aliases(\?|$)/;

export interface RestClientOptions {
  /**
   * REST base incl. version prefix, e.g. "/api/v1" (no trailing slash), or a
   * `RestOrigin` that re-resolves per request — that is how the brain makes
   * REST follow the endpoint the signaling race won (docs/04 §2).
   */
  baseUrl: string | RestOrigin;
  getToken: AccessTokenProvider;
  /** Injectable for tests. */
  fetchFn?: typeof fetch;
  /**
   * The signaling socket's tunnel, when the host has one. Authenticated calls
   * prefer it whenever it is ready — that is the entire point of having it —
   * and fall back to HTTPS unchanged when it is not. A function is accepted so
   * wiring can be lazy: the engine that owns the socket is usually built after
   * this client.
   */
  rpc?: RpcTransport | (() => RpcTransport | null | undefined);
}

export class RestClient {
  private readonly origin: RestOrigin;
  private readonly getToken: AccessTokenProvider;
  private readonly fetchFn: typeof fetch;
  private readonly rpcSource: (() => RpcTransport | null | undefined) | null;

  constructor(options: RestClientOptions) {
    this.origin =
      typeof options.baseUrl === 'string' ? staticOrigin(options.baseUrl) : options.baseUrl;
    this.getToken = options.getToken;
    const rpc = options.rpc;
    this.rpcSource = rpc === undefined ? null : typeof rpc === 'function' ? rpc : () => rpc;
    // Bounded + retrying by default (lib/http.ts): an unbounded fetch on a
    // stalled mobile link hangs past the native bridge's call timeout and
    // surfaces as "not connected" instead of a network error. Wrapped in
    // `routeFailures` by construction so no call site can send to a dead
    // origin forever — a static base makes that wrapper a no-op.
    this.fetchFn = routeFailures(this.origin, options.fetchFn ?? defaultFetch());
  }

  /** Base the NEXT request will use — re-resolved per request, never cached. */
  private get baseUrl(): string {
    return this.origin.base();
  }

  /** The tunnel, but only when it would actually carry this route now. */
  private socketFor(path: string): RpcTransport | null {
    if (this.rpcSource === null || HTTPS_ONLY_ROUTE.test(path)) return null;
    const transport = this.rpcSource();
    return transport && transport.ready() ? transport : null;
  }

  /**
   * An authenticated call, over the socket when there is one and over HTTPS
   * otherwise.
   *
   * The socket is preferred whenever it is ready, because being free of a
   * handshake is the entire reason it exists. Note what it does NOT do: it
   * never asks `getToken()` — the socket proved this session's identity at
   * `sys.auth` and the server dispatches with it, so a request that rides it
   * involves no HTTPS at all, not even the refresh that acquiring a token can
   * trigger. On a link where new flows die, that is the difference between a
   * working screen and a spinner. The single exception is the path below where
   * the socket's OWN credential is what failed, and only the session can supply
   * a newer one.
   *
   * That path is also why a tunnelled 401 is not trusted the way an HTTPS 401
   * is: the tunnel authorizes per request against a 15-minute token on a socket
   * with no maximum lifetime, so "this socket's credential aged out" is the
   * likeliest reading and it is recoverable. It is recovered EXACTLY once —
   * renew, then re-issue only because the credential actually changed, so a
   * server that keeps saying 401 cannot produce a second attempt — and what
   * survives that is a transport failure rather than an answer, which is what
   * lets HTTPS carry it (`staleTunnelCredential`, `mayReissueOverHttps`).
   *
   * It also never feeds `RestOrigin`: what the socket manages to carry says
   * nothing about whether an HTTPS origin is alive, and letting it vote would
   * corrupt the failover the origin router runs on real evidence (docs/04 §2).
   */
  private async request<S extends z.ZodType>(
    method: Method,
    path: string,
    responseSchema: S,
    body?: unknown,
  ): Promise<z.infer<S>> {
    const socket = this.socketFor(path);
    if (socket !== null) {
      const send = (): Promise<RpcOutcome> =>
        socket.send({
          method,
          path,
          ...(body !== undefined ? { body } : {}),
        });
      let outcome = await send();
      if (staleTunnelCredential(path, outcome)) {
        // Renewal is the whole retry budget: `renewAuth` resolves true only when
        // a DIFFERENT credential reached the socket, so this can run once per
        // token generation and never in a loop.
        const renewed = (await socket.renewAuth?.()) === true;
        outcome = renewed && socket.ready() ? await send() : TUNNEL_UNAUTHORIZED;
        // A 401 that survives a fresh credential is not this socket's age. It is
        // not re-issued on the socket either — HTTPS decides what happens to it,
        // exactly as it would if the tunnel had never existed.
        if (staleTunnelCredential(path, outcome)) outcome = TUNNEL_UNAUTHORIZED;
      }
      // An answer is an answer, whatever its status: the server ran the route,
      // so re-issuing it over HTTPS would double-apply it. The statuses the
      // GATEWAY invents for its own refusals (501 no dispatcher, 502 response
      // over the frame ceiling) never arrive here as `ok` — the transport
      // classifies those as failures, because on those the route did not run or
      // its answer never made it onto the wire.
      if (outcome.ok) return decodeRpc(path, outcome.status, outcome.body, responseSchema);
      if (!mayReissueOverHttps(method, path, outcome)) throw rpcFailed(outcome.failure);
      // The socket attempt WAS this chain's first attempt, so the HTTPS leg
      // behind it is the last one: patient, and never replayed again
      // (lib/http.ts `attemptDeadlinesMs` — a lone attempt gets the wide budget,
      // and the total still has to fit the native bridge's 30 s reap).
      return this.overHttps(method, path, responseSchema, body, true);
    }
    return this.overHttps(method, path, responseSchema, body, false);
  }

  private async overHttps<S extends z.ZodType>(
    method: Method,
    path: string,
    responseSchema: S,
    body: unknown,
    lastAttempt: boolean,
  ): Promise<z.infer<S>> {
    const token = await this.getToken();
    const headers: Record<string, string> = { authorization: `Bearer ${token}` };
    // `retry` is lib/http.ts's per-request opt-out, invisible to the wire and
    // ignored by an injected plain fetch. Set only behind a socket attempt: the
    // chain has already spent a leg, and nothing that already went out once may
    // go out twice here.
    const init: ResilientInit = lastAttempt ? { method, headers, retry: false } : { method, headers };
    if (body !== undefined) {
      headers['content-type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    const at = this.baseUrl;
    const response = await this.fetchFn(`${at}${path}`, init);
    if (!response.ok) {
      let code: ErrorCode | undefined;
      let message = `request to ${path} failed with status ${response.status}`;
      try {
        const parsed = ApiErrorSchema.safeParse(await response.json());
        if (parsed.success) {
          code = parsed.data.code;
          message = parsed.data.message;
        }
      } catch {
        // Non-JSON error body — keep the generic message.
      }
      // A coded body is our server's signature; anything else on an unproven
      // origin means this host is not the API (see RestOrigin.noteResponse).
      this.origin.noteResponse(at, code !== undefined);
      throw new RestError(response.status, code, message);
    }
    if (response.status === 204) return responseSchema.parse(undefined);
    const parsed = responseSchema.safeParse(await response.json());
    if (!parsed.success) {
      throw new RestError(response.status, undefined, `malformed response from ${path}`);
    }
    return parsed.data;
  }

  // --- auth (unauthenticated) ------------------------------------------------

  /**
   * `POST /auth/challenge` — no bearer token (docs/02 §3). Screens use it for
   * the fresh-key-signature guards on `PUT /me/password` and `DELETE /me`
   * (docs/03 §3a/§3b); J7's AuthClient keeps its own copy for login.
   */
  async authChallenge(userId: string): Promise<AuthChallengeResponse> {
    const at = this.baseUrl;
    const response = await this.fetchFn(`${at}/auth/challenge`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ userId }),
    });
    if (!response.ok) {
      this.origin.noteResponse(at, await authoredByServer(response));
      throw new RestError(response.status, undefined, 'challenge request failed');
    }
    const parsed = AuthChallengeResponse.safeParse(await response.json());
    if (!parsed.success) {
      throw new RestError(response.status, undefined, 'malformed response from /auth/challenge');
    }
    return parsed.data;
  }

  /** Unauthenticated POST helper for the pre-login passkey endpoints (which,
   * like `/auth/challenge`, carry no bearer token). */
  private async postUnauth<S extends z.ZodType>(
    path: string,
    body: unknown,
    responseSchema: S,
  ): Promise<z.infer<S>> {
    const at = this.baseUrl;
    const response = await this.fetchFn(`${at}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      let code: ErrorCode | undefined;
      let message = `request to ${path} failed with status ${response.status}`;
      try {
        const parsed = ApiErrorSchema.safeParse(await response.json());
        if (parsed.success) {
          code = parsed.data.code;
          message = parsed.data.message;
        }
      } catch {
        // Non-JSON error body — keep the generic message.
      }
      this.origin.noteResponse(at, code !== undefined);
      throw new RestError(response.status, code, message);
    }
    const parsed = responseSchema.safeParse(await response.json());
    if (!parsed.success) {
      throw new RestError(response.status, undefined, `malformed response from ${path}`);
    }
    return parsed.data;
  }

  // --- passkeys (WebAuthn, docs/03 §2 — server sessions only) ----------------

  /** `POST /auth/passkeys/register/options` (auth'd) — opaque creation options. */
  registerPasskeyOptions(): Promise<PasskeyRegisterOptionsResponse> {
    return this.request('POST', '/auth/passkeys/register/options', PasskeyRegisterOptionsResponse, {});
  }

  /** `POST /auth/passkeys/register` (auth'd) — store the new credential. */
  registerPasskey(body: PasskeyRegisterRequest): Promise<PasskeyRegisterResponse> {
    return this.request('POST', '/auth/passkeys/register', PasskeyRegisterResponse, body);
  }

  /** `GET /auth/passkeys` (auth'd) — the caller's registered passkeys. */
  listPasskeys(): Promise<PasskeysListResponse> {
    return this.request('GET', '/auth/passkeys', PasskeysListResponse);
  }

  /** `DELETE /auth/passkeys/:id` (auth'd). */
  deletePasskey(id: string): Promise<void> {
    return this.request('DELETE', `/auth/passkeys/${id}`, VoidResponse).then(() => undefined);
  }

  /** `POST /auth/passkeys/login/options` — no auth (pre-login). */
  passkeyLoginOptions(userId: string): Promise<PasskeyLoginOptionsResponse> {
    return this.postUnauth('/auth/passkeys/login/options', { userId }, PasskeyLoginOptionsResponse);
  }

  /** `POST /auth/passkeys/login` — no auth; mints a session (TokenBundle). */
  passkeyLogin(
    userId: string,
    credential: PasskeyLoginRequest['credential'],
    kind?: SessionKind,
  ): Promise<PasskeyLoginResponse> {
    return this.postUnauth(
      '/auth/passkeys/login',
      { userId, credential, ...(kind ? { kind } : {}) },
      PasskeyLoginResponse,
    );
  }

  // --- account -------------------------------------------------------------

  getMe(): Promise<MeResponse> {
    return this.request('GET', '/me', MeResponse);
  }

  /** Partial `PATCH /me` — at least one of displayName / avatarSeed. */
  updateMe(body: UpdateMeRequest): Promise<MeResponse> {
    return this.request('PATCH', '/me', MeResponse, body);
  }

  /** `PUT /me/password` (docs/02 §3): set, change, or remove (all-null body). */
  setPassword(body: SetPasswordRequest): Promise<void> {
    return this.request('PUT', '/me/password', VoidResponse, body).then(() => undefined);
  }

  deleteMe(body: DeleteMeRequest): Promise<void> {
    return this.request('DELETE', '/me', VoidResponse, body).then(() => undefined);
  }

  // --- contact-alias cloud sync (docs/08 §8.2) -------------------------------
  // The E2E-encrypted alias blob is opaque to the server (like SDP): the client
  // encrypts under a seed-derived key it never holds. See aliasSync/.

  /** `GET /me/aliases` — the account's alias blob (both fields null ⇔ never uploaded). */
  getAliasesBlob(): Promise<AliasesBlobResponse> {
    return this.request('GET', '/me/aliases', AliasesBlobResponse);
  }

  /** `PUT /me/aliases` — whole-blob replace (LWW); returns the server timestamp. */
  putAliasesBlob(blob: string): Promise<PutAliasesBlobResponse> {
    return this.request('PUT', '/me/aliases', PutAliasesBlobResponse, {
      blob,
    } satisfies PutAliasesBlobRequest);
  }

  // --- friends ---------------------------------------------------------------

  listFriends(): Promise<FriendsListResponse> {
    return this.request('GET', '/friends', FriendsListResponse);
  }

  sendFriendRequest(body: SendFriendRequestRequest): Promise<void> {
    return this.request('POST', '/friends/requests', VoidResponse, body).then(() => undefined);
  }

  listFriendRequests(): Promise<FriendRequestsListResponse> {
    return this.request('GET', '/friends/requests', FriendRequestsListResponse);
  }

  acceptFriendRequest(id: string): Promise<void> {
    return this.request('POST', `/friends/requests/${id}/accept`, VoidResponse).then(() => undefined);
  }

  declineFriendRequest(id: string): Promise<void> {
    return this.request('POST', `/friends/requests/${id}/decline`, VoidResponse).then(() => undefined);
  }

  unfriend(userId: string): Promise<void> {
    return this.request('DELETE', `/friends/${userId}`, VoidResponse).then(() => undefined);
  }

  // --- blocks ------------------------------------------------------------------

  listBlocks(): Promise<BlocksListResponse> {
    return this.request('GET', '/blocks', BlocksListResponse);
  }

  block(body: BlockRequest): Promise<void> {
    return this.request('POST', '/blocks', VoidResponse, body).then(() => undefined);
  }

  unblock(userId: string): Promise<void> {
    return this.request('DELETE', `/blocks/${userId}`, VoidResponse).then(() => undefined);
  }

  // --- calls ---------------------------------------------------------------------

  listCalls(params?: { limit?: number; before?: string }): Promise<CallsListResponse> {
    const query = new URLSearchParams();
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.before !== undefined) query.set('before', params.before);
    const qs = query.toString();
    return this.request('GET', `/calls${qs ? `?${qs}` : ''}`, CallsListResponse);
  }

  markCallsSeen(body: CallsSeenRequest = {}): Promise<void> {
    return this.request('POST', '/calls/seen', VoidResponse, body).then(() => undefined);
  }

  // --- sessions --------------------------------------------------------------------

  listSessions(): Promise<SessionsListResponse> {
    return this.request('GET', '/sessions', SessionsListResponse);
  }

  updateCurrentSession(body: UpdateSessionRequest): Promise<void> {
    return this.request('PATCH', '/sessions/current', VoidResponse, body).then(() => undefined);
  }

  terminateSession(id: string): Promise<void> {
    return this.request('DELETE', `/sessions/${id}`, VoidResponse).then(() => undefined);
  }

  terminateOtherSessions(): Promise<void> {
    return this.request('DELETE', '/sessions?others=true', VoidResponse).then(() => undefined);
  }

  // --- TURN --------------------------------------------------------------------

  /** `POST /turn/credentials` — feeds the engine's `IceServersProvider` at wiring. */
  turnCredentials(): Promise<TurnCredentialsResponse> {
    return this.request('POST', '/turn/credentials', TurnCredentialsResponse, {});
  }
}

// Some endpoints return an empty (or ignorable) body on success — accept
// anything without asserting a wire shape that docs/02 doesn't define.
const VoidResponse = { safeParse: (v: unknown) => ({ success: true as const, data: v }), parse: (v: unknown) => v } as unknown as z.ZodType<unknown>;
